import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Plus, Check, Sparkles, Trash2, Download } from "lucide-react";
import { PRIORITIES } from "./constants";

const prioCls = {
  urgent: "bg-rose-100 text-rose-700", important: "bg-amber-100 text-amber-700",
  normal: "bg-secondary text-muted-foreground", later: "bg-slate-100 text-slate-600",
};

export default function Tasks() {
  const t = useT();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [prio, setPrio] = useState("normal");
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = async () => { try { const { data } = await api.get("/coach/assistant/tasks"); setTasks(data.tasks || []); } catch { /* noop */ } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!title.trim()) return;
    setAdding(true);
    try { await api.post("/coach/assistant/tasks", { title, priority: prio }); setTitle(""); setPrio("normal"); await load(); }
    catch (err) { toast.error(apiError(err)); } finally { setAdding(false); }
  };
  const done = async (id) => { setBusy(id); try { await api.patch(`/coach/assistant/tasks/${id}`, { status: "done" }); await load(); } catch (err) { toast.error(apiError(err)); } finally { setBusy(null); } };
  const del = async (id) => { setBusy(id); try { await api.delete(`/coach/assistant/tasks/${id}`); await load(); } catch (err) { toast.error(apiError(err)); } finally { setBusy(null); } };
  const delegate = async (id) => {
    setBusy(id);
    try { const { data } = await api.post(`/coach/assistant/tasks/${id}/delegate`); setTasks((ts) => ts.map((x) => (x.id === id ? data : x))); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(null); }
  };
  const exportCsv = async () => {
    try {
      const res = await api.get("/coach/assistant/tasks/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "ai-praxis-tasks.csv"; a.click(); URL.revokeObjectURL(url);
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="space-y-4" data-testid="aipa-tasks">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl text-foreground">{t("aipa.tasks_title")}</h2>
        <button onClick={exportCsv} className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs text-muted-foreground hover:bg-accent" data-testid="aipa-task-export"><Download className="h-3.5 w-3.5" /> {t("aipa.task_export")}</button>
      </div>
      <div className="flex flex-wrap gap-2 rounded-2xl border border-border bg-card p-3">
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("aipa.task_title_ph")} className="min-w-[180px] flex-1" data-testid="aipa-task-title" onKeyDown={(e) => e.key === "Enter" && add()} />
        <select value={prio} onChange={(e) => setPrio(e.target.value)} className="rounded-lg border border-border bg-background px-3 text-sm text-foreground" data-testid="aipa-task-prio">
          {PRIORITIES.map((p) => <option key={p} value={p}>{t(`aipa.prio.${p}`)}</option>)}
        </select>
        <Button className="gap-1.5 rounded-full" disabled={adding} onClick={add} data-testid="aipa-task-add"><Plus className="h-4 w-4" /> {adding ? t("aipa.task_adding") : t("aipa.task_add")}</Button>
      </div>
      {loading ? <Skeleton className="h-32 rounded-2xl" /> : tasks.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center" data-testid="aipa-tasks-empty"><p className="text-sm text-muted-foreground">{t("aipa.tasks_empty")}</p></div>
      ) : (
        <div className="space-y-2">
          {tasks.map((task) => (
            <div key={task.id} className="rounded-2xl border border-border bg-card p-4" data-testid={`aipa-task-${task.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${prioCls[task.priority] || prioCls.normal}`}>{t(`aipa.prio.${task.priority}`)}</span>
                    <p className="truncate text-sm font-medium text-foreground">{task.title}</p>
                  </div>
                  {task.description && <p className="mt-1 text-xs text-muted-foreground">{task.description}</p>}
                  {task.status === "delegated" && task.ai_result && (
                    <div className="mt-2 rounded-lg bg-secondary/50 p-2">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-primary">{t("aipa.task_ai_result")}</p>
                      <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">{task.ai_result}</p>
                    </div>
                  )}
                </div>
                <div className="flex shrink-0 gap-1">
                  <button title={t("aipa.task_delegate")} disabled={busy === task.id} onClick={() => delegate(task.id)} data-testid={`aipa-task-delegate-${task.id}`} className="rounded-lg border border-border p-1.5 text-primary hover:bg-accent disabled:opacity-50"><Sparkles className="h-4 w-4" /></button>
                  <button title={t("aipa.task_done")} disabled={busy === task.id} onClick={() => done(task.id)} data-testid={`aipa-task-done-${task.id}`} className="rounded-lg border border-border p-1.5 text-emerald-600 hover:bg-accent disabled:opacity-50"><Check className="h-4 w-4" /></button>
                  <button title={t("aipa.task_delete")} disabled={busy === task.id} onClick={() => del(task.id)} data-testid={`aipa-task-delete-${task.id}`} className="rounded-lg border border-border p-1.5 text-rose-500 hover:bg-accent disabled:opacity-50"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
